// src/estimates.js
import { useCallback, useMemo, useState, useEffect } from 'react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
import EstimateList from './estimatelist';
import EstimateEntry from './estimateentry';
import InvoicePrint from './invoiceprint';
import { createDocumentDuplicateDraft } from './documentDuplicateDraft';
import TitleBar from './components/ui/TitleBar';
import BackToDashboardButton from './components/ui/BackToDashboardButton';
import OperationalListControls from './components/OperationalListControls';
import SavedToastOverlay from './components/SavedToastOverlay';

/* ===============================
   DISCOUNT PARSER
   - supports numeric OR selection string
================================== */
const parseDiscountSelection = (sel, subtotal) => {
  try {
    const s = String(sel || "0").trim();
    if (!s || s === "0") return 0;

    // numeric
    if (!s.includes(":")) {
      const v = Number(s);
      return Number.isFinite(v) ? v : 0;
    }

    const parts = s.split(":");
    const type = String(parts[0] || "fixed").toLowerCase();
    const rawVal = Number(parts[1] || 0);
    const sub = Number(subtotal || 0);
    const val = Number.isFinite(rawVal) ? rawVal : 0;

    const rawDiscount = (type === "percentage") ? (sub * (val / 100)) : val;
    const discount = Number.isFinite(rawDiscount) ? rawDiscount : 0;
    return Math.min(Math.max(discount, 0), sub);
  } catch (e) {
    return 0;
  }
};


/* ===============================
   HELPERS
================================== */
const safeUUID = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {}
  return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const addDaysISO = (iso, days) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
};

const dueFromTerms = (invoiceDateISO, terms) => {
  const t = (terms || 'COD').toString().toUpperCase().trim();
  if (t === 'NET15' || t === 'NET 15') return addDaysISO(invoiceDateISO, 15);
  if (t === 'NET30' || t === 'NET 30') return addDaysISO(invoiceDateISO, 30);
  return invoiceDateISO;
};

/* ===============================
   TOTALS
================================== */
const recalcTotals = (est) => {
  const items = Array.isArray(est.items) ? est.items : [];
  const billable = items.filter(i => i.type !== 'note');

  const subtotal = billable.reduce((acc, i) => {
    const qty = Number(i.qty || 1);
    const rate = Number(i.rate || 0);
    return acc + (qty * rate);
  }, 0);

  const discount = parseDiscountSelection(est.discount, subtotal);
  const afterDisc = subtotal - discount;

  const stateTax = Number(est.sales_tax_rate || 0);
  const localTax = Number(est.county_sales_tax_rate || 0);

  const taxableSubtotal = billable.reduce((acc, i) => {
    if (i.taxable !== true) return acc;
    const qty = Number(i.qty || 1);
    const rate = Number(i.rate || 0);
    return acc + qty * rate;
  }, 0);

  let taxableAfterDisc = taxableSubtotal;
  if (subtotal > 0 && discount > 0) {
    const ratio = Math.max(0, Math.min(1, taxableSubtotal / subtotal));
    taxableAfterDisc = Math.max(0, taxableSubtotal - (discount * ratio));
  }

  const stateTaxAmount = taxableAfterDisc * (stateTax / 100);
  const countyTaxAmount = taxableAfterDisc * (localTax / 100);

  const total = afterDisc + stateTaxAmount + countyTaxAmount;

  return {
    ...est,
    items,
    subtotal,
    discount,
    stateTaxAmount,
    countyTaxAmount,
    total,
    status: est.status || 'draft'
  };
};

// NEW estimates: estimate_number blank, backend assigns.
const newEstimateTemplate = () =>
  recalcTotals({
    id: safeUUID(),
    estimate_number: '',
    customer_id: null,
    customer_name: '',
    company: '',
    email: '',
    office_phone: '',
    mobile_phone: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    address: '',
    terms: 'COD',
    notes: '',
    items: [],
    subtotal: 0,
    discount: 0,
    sales_tax_rate: 0,
    county_sales_tax_rate: 0,
    county_name: '',
    selected_local_tax_ids: [],
    total: 0,
    status: 'draft'
  });

/* ===============================
   DB API HELPERS (TENANT SAFE)
================================== */
const apiFetchJson = async (path, options = {}) => {
  const API = getApiBase();
  const res = await fetch(`${API}${path}`, {
    method: 'GET',
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache', ...getTenantHeaders() },
    signal: options.signal
  });
  if (!res.ok) throw new Error(`GET ${path} failed (${res.status})`);
  return await res.json();
};

const apiPost = async (path, body) => {
  const API = getApiBase();
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
    body: JSON.stringify(body || {})
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`POST ${path} failed (${res.status}) ${t}`);
  }
  return await res.json().catch(() => ({ ok: true }));
};

const apiPatch = async (path, body) => {
  const API = getApiBase();
  const res = await fetch(`${API}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
    body: JSON.stringify(body || {})
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`PATCH ${path} failed (${res.status}) ${t}`);
  }
  return await res.json().catch(() => ({ ok: true }));
};

const apiDelete = async (path) => {
  const API = getApiBase();
  const res = await fetch(`${API}${path}`, {
    method: 'DELETE',
    headers: { ...getTenantHeaders() }
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`DELETE ${path} failed (${res.status}) ${t}`);
  }
  return await res.json().catch(() => ({ ok: true }));
};

const fetchFirstWorking = async (paths, fallbackValue) => {
  for (const p of paths) {
    try {
      const data = await apiFetchJson(p);
      return data;
    } catch (e) {}
  }
  return fallbackValue;
};

/* ===============================
   COMPONENT
================================== */
const Estimates = ({ navigate, openNewEstimate = false, onNewEstimateOpened }) => {
  const [view, setView] = useState(openNewEstimate ? 'entry' : 'list');
  const [estimates, setEstimates] = useState([]);
  const [estimateListYear, setEstimateListYear] = useState(() => new Date().getFullYear());
  const [estimateListSearchDraft, setEstimateListSearchDraft] = useState('');
  const [estimateListSearch, setEstimateListSearch] = useState('');
  const [estimateListPage, setEstimateListPage] = useState(1);
  const [estimateListPagination, setEstimateListPagination] = useState({
    page: 1,
    page_size: 50,
    total: 0,
    total_pages: 1
  });
  const [availableEstimateYears, setAvailableEstimateYears] = useState([]);
  const [estimateListVersion, setEstimateListVersion] = useState(0);

  // used by EstimateEntry
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [cannedJobs, setCannedJobs] = useState([]);
  const [counties] = useState([]);
  const [coupons] = useState([]);

  const [estimateDraft, setEstimateDraft] = useState(() => newEstimateTemplate());
  const [printEstimate, setPrintEstimate] = useState(null);

  const [hoverKey, setHoverKey] = useState('');

  useEffect(() => {
    if (!openNewEstimate) return;
    if (typeof onNewEstimateOpened === 'function') onNewEstimateOpened();
  }, [openNewEstimate, onNewEstimateOpened]);

  const styles = {
    container: { padding: '18px', maxWidth: '1400px', margin: '0 auto', color: 'black' },

    newBtn: {
      backgroundColor: '#1B4332',
      color: '#FFA500',
      padding: '16px 18px',
      fontWeight: 900,
      border: '6px solid #FFA500',
      cursor: 'pointer',
      fontSize: '20px',
      textTransform: 'uppercase'
    },

    backBtn: {
      fontWeight: 900,
      padding: '14px 20px',
      border: '4px solid black',
      cursor: 'pointer',
      backgroundColor: '#FFA500',
      color: 'black',
      fontSize: '16px',
      textTransform: 'uppercase'
    },

    viewToolbar: {
      display: 'flex',
      gap: '12px',
      marginBottom: '18px',
      flexWrap: 'wrap'
    },

    printBtn: {
      backgroundColor: 'black',
      color: '#FFA500',
      padding: '14px 20px',
      fontWeight: 900,
      border: '4px solid #E8761D',
      cursor: 'pointer',
      fontSize: '16px',
      textTransform: 'uppercase'
    },
    editBtn: {
      backgroundColor: '#1B4332',
      color: '#FFA500',
      padding: '14px 20px',
      fontWeight: 900,
      border: '4px solid #E8761D',
      cursor: 'pointer',
      fontSize: '16px',
      textTransform: 'uppercase'
    },

    printNote: {
      fontWeight: 900,
      fontSize: '14px',
      marginBottom: '12px',
      padding: '10px',
      border: '3px solid black',
      backgroundColor: '#fff7ed'
    }
  };

  const hoverBlue = (base, key) => {
    if (hoverKey !== key) return base;
    if (key === 'create') {
      return { ...base, backgroundColor: '#FFC107', color: 'black', borderColor: '#FFA500' };
    }
    if (key === 'back' || key === 'back2') {
      return { ...base, backgroundColor: '#FFC107', color: 'black', borderColor: 'black' };
    }
    return { ...base, backgroundColor: '#FFC107', color: 'black', borderColor: '#FFA500' };
  };

  const loadEstimatesFromDB = useCallback(async ({ signal } = {}) => {
    const query = new URLSearchParams({
      page: String(estimateListPage),
      page_size: '50'
    });

    if (estimateListSearch.trim()) {
      query.set('search', estimateListSearch);
    } else {
      query.set('year', String(estimateListYear));
    }

    const data = await apiFetchJson(`/api/estimates?${query.toString()}`, { signal });
    const normalized = (data.rows || []).map(e => {
      const tag = String(e.notes || '').match(/\[DISCOUNT:([^\]]+)\]/);
      return recalcTotals({
        ...e,
        id: e.external_id || e.id,
        discount: tag ? tag[1] : (e.discount || 0),
        discount_selection: e.discount_selection || '',
      });
    });
    setEstimates(normalized);
    setEstimateListPagination(data.pagination || {
      page: 1,
      page_size: 50,
      total: 0,
      total_pages: 1
    });
    setAvailableEstimateYears(
      Array.isArray(data.available_years)
        ? data.available_years.map(Number).filter((year) => Number.isInteger(year))
        : []
    );
    return normalized;
  }, [estimateListYear, estimateListSearch, estimateListPage]);

  const refreshEstimateList = () => {
    setEstimateListVersion((version) => version + 1);
  };


  const loadSupportLists = async () => {
    const dbCustomers = await fetchFirstWorking(['/api/customers'], []);
    const dbInventory = await fetchFirstWorking(['/api/inventory'], []);
    const dbCannedJobs = await fetchFirstWorking(
      ['/api/canned-jobs', '/api/cannedjobs', '/api/canned_jobs', '/api/cannedJobs'],
      []
    );

    setCustomers(Array.isArray(dbCustomers) ? dbCustomers : []);
    setInventory(Array.isArray(dbInventory) ? dbInventory : []);
    setCannedJobs(Array.isArray(dbCannedJobs) ? dbCannedJobs : []);

  };

  useEffect(() => {
    if (!estimateListSearchDraft.trim()) {
      setEstimateListSearch('');
      setEstimateListPage(1);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setEstimateListSearch(estimateListSearchDraft);
      setEstimateListPage(1);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [estimateListSearchDraft]);

  useEffect(() => {
    (async () => {
      try {
        await loadSupportLists();
      } catch (e) {
        console.error('Estimate support-list load failed:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    loadEstimatesFromDB({ signal: controller.signal }).catch((e) => {
      if (e.name === 'AbortError') return;
      console.error('Estimates DB load failed:', e);
      setEstimates([]);
    });

    return () => controller.abort();
  }, [loadEstimatesFromDB, estimateListVersion]);

  const hydrateReturnedEstimateNumber = (final, serverResponse) => {
    try {
      if (!serverResponse || typeof serverResponse !== 'object') return final;

      const returned = String(serverResponse.estimate_number || serverResponse.estimateNumber || '').trim();
      if (!returned) return final;

      return recalcTotals({ ...final, estimate_number: returned });
    } catch (e) {
      return final;
    }
  };

  const handleSaveEstimate = async (draft) => {
    const wasCreation = !String(draft.estimate_number || '').trim();
    const discStr = String(draft.discount || '0');
    const syncedDraft = discStr.includes(':') ? { ...draft, discount_selection: discStr } : draft;
    const final = recalcTotals(syncedDraft);
    const subtotalNum = Number(final.subtotal || 0);

    const payload = {
      ...final,
      external_id: final.id,
      estimate_number: String(final.estimate_number || ''), // blank ok -> backend assigns

      subtotal: Number.isFinite(subtotalNum) ? subtotalNum : 0,
      discount: parseDiscountSelection(final.discount, subtotalNum),

      sales_tax_rate: Number(final.sales_tax_rate || 0),
      county_sales_tax_rate: Number(final.county_sales_tax_rate || 0),
      county_name: String(final.county_name || ''),
      selected_local_tax_ids: Array.isArray(final.selected_local_tax_ids)
        ? [...final.selected_local_tax_ids]
        : null,

      total: Number(final.total || 0),
    };

    const saved = await apiPost('/api/estimates', payload);

    const hydrated = hydrateReturnedEstimateNumber(final, saved);
    setEstimateDraft(hydrated);

    refreshEstimateList();
    setView('list');

    if (saved?.ok === true && String(saved.estimate_number || '').trim()) {
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: wasCreation ? 'ESTIMATE CREATED!' : 'ESTIMATE UPDATED!' }
      }));
    }
  };

  const handleDeleteEstimate = async (id) => {
    await apiDelete(`/api/estimates/${encodeURIComponent(String(id))}`);
    if (estimates.length === 1 && estimateListPage > 1) {
      setEstimateListPage((page) => page - 1);
    } else {
      refreshEstimateList();
    }
  };

  const handleEditEstimate = (est) => {
    setEstimateDraft(recalcTotals(est));
    setView('entry');
  };

  const handleDuplicateEstimate = (sourceEstimate) => {
    const duplicate = createDocumentDuplicateDraft({
      documentType: 'estimate',
      sourceDocument: sourceEstimate,
      freshDocument: newEstimateTemplate(),
      createId: safeUUID
    });

    setEstimateDraft(recalcTotals(duplicate));
    setView('entry');
  };

  const handleViewPrint = (est) => {
    const latest = estimates.find(e => String(e.id) === String(est.id)) || est;
    const ready = recalcTotals(latest);
    setPrintEstimate(ready);
    setView('print');
  };

  const handleSetStatus = async (est, nextStatus) => {
    await apiPatch(`/api/estimates/${encodeURIComponent(String(est.id))}/status`, { status: nextStatus });
    refreshEstimateList();
    if (nextStatus === 'approved' || nextStatus === 'declined') {
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: nextStatus === 'approved' ? 'ESTIMATE APPROVED!' : 'ESTIMATE DECLINED!' }
      }));
    }
  };

  const handleConvertToInvoice = async (est) => {
    try {
      const freshEstimate = recalcTotals(
        estimates.find(e => String(e.id) === String(est.id)) || est
      );

      let terms = (freshEstimate.terms || '').toString().trim();
      if (!terms && freshEstimate.customer_id) {
        const c = (customers || []).find(x => String(x.id) === String(freshEstimate.customer_id));
        if (c && (c.terms || c.customer_terms)) terms = String(c.terms || c.customer_terms);
      }
      if (!terms) terms = 'COD';

      const invoice_date = todayISO();
      const due_date = dueFromTerms(invoice_date, terms);

      // IMPORTANT: do NOT generate invoice number on frontend
      const invoiceDraft = {
        external_id: safeUUID(),
        customer_id: freshEstimate.customer_id ?? null,
        invoice_number: '',

        invoice_date,
        due_date,
        terms,

        customer_name: freshEstimate.customer_name || '',
        company: freshEstimate.company || '',
        customer_email: freshEstimate.email || '',
        customer_phone: freshEstimate.mobile_phone || freshEstimate.office_phone || '',
        customer_address: freshEstimate.address || '',

        notes: freshEstimate.notes || '',

        subtotal: Number(freshEstimate.subtotal || 0),
        discount: (() => {
          const tag = String(freshEstimate.notes || '').match(/\[DISCOUNT:([^\]]+)\]/);
          return tag ? tag[1] : parseDiscountSelection(freshEstimate.discount, Number(freshEstimate.subtotal || 0));
        })(),
        sales_tax_rate: Number(freshEstimate.sales_tax_rate || 0),
        county_sales_tax_rate: Number(freshEstimate.county_sales_tax_rate || 0),
        county_name: freshEstimate.county_name || '',
        state_abbr: freshEstimate.state_abbr || '',
        state_name: freshEstimate.state_name || '',
        selected_local_tax_ids: Array.isArray(freshEstimate.selected_local_tax_ids)
          ? [...freshEstimate.selected_local_tax_ids]
          : null,
        total: Number(freshEstimate.total || 0),

        amount_paid: 0,
        expected_amount_paid: 0,
        balance_due: Number(freshEstimate.total || 0),

        status: 'UNPAID',
        stateTaxAmount: Number(freshEstimate.stateTaxAmount || 0),
        countyTaxAmount: Number(freshEstimate.countyTaxAmount || 0),

        items_json: JSON.stringify(Array.isArray(freshEstimate.items) ? freshEstimate.items : []),
        from_estimate_id: freshEstimate.id,
      };

      if (typeof navigate !== 'function') {
        throw new Error('Invoice navigation is unavailable.');
      }

      navigate('invoices', { invoiceDraft });
    } catch (e) {
      alert(`Failed to convert estimate to invoice: ${e.message}`);
    }
  };

  const printArea = useMemo(() => {
    if (view !== 'print' || !printEstimate) return null;

    return (
      <>
        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            #jf-est-print-area, #jf-est-print-area * { visibility: visible !important; }
            #jf-est-print-area { position: fixed !important; left: 0 !important; top: 0 !important; width: 100% !important; }
          }
        `}</style>

        <div id="jf-est-print-area">
          <InvoicePrint invoice={printEstimate} docType="ESTIMATE" />
        </div>
      </>
    );
  }, [view, printEstimate]);

  return (
    <div className={view === 'print' ? undefined : 'jfn-estimates-page'} style={styles.container}>
      <SavedToastOverlay accentColor="#6B8E23" />

      <style>{`
        @media (max-width: 767px) {
          .jfn-estimates-page {
            padding-left: 6px !important;
            padding-right: 6px !important;
          }
        }
      `}</style>
      {view === 'list' && (
        <>
          <style>{`
            .jf-back-btn:hover {
              background-color: #FFC107 !important;
              border-color: black !important;
              color: black !important;
            }
          `}</style>

          <TitleBar
            icon="📋"
            title={`ESTIMATES (${Number(estimateListPagination.total || 0)})`}
            accent="#6B8E23"
            right={
              <div style={{ display: 'flex', gap: '12px', flexDirection: window.innerWidth < 768 ? 'column' : 'row', alignItems: window.innerWidth < 768 ? 'stretch' : 'center' }}>
                <BackToDashboardButton onClick={() => (window.location.href = '/dashboard')} />

                <button
                  style={hoverBlue(styles.newBtn, 'create')}
                  onMouseEnter={() => setHoverKey('create')}
                  onMouseLeave={() => setHoverKey('')}
                  onClick={() => {
                    setEstimateDraft(newEstimateTemplate());
                    setView('entry');
                  }}
                >
                  + CREATE ESTIMATE
                </button>
              </div>
            }
          />

          <EstimateList
            estimates={(estimates || []).map(recalcTotals)}
            isGlobalSearchActive={Boolean(estimateListSearch.trim())}
            listControls={
              <OperationalListControls
                moduleName="Estimates"
                searchPlaceholder="Cust, Est #, Phone, Email"
                showResultCount={false}
                theme={{
                  border: '#6B8E23',
                  accent: '#6B8E23',
                  buttonBackground: '#6B8E23',
                  buttonText: '#000',
                  buttonHover: '#A8C66C',
                  divider: '#6B8E23'
                }}
                selectedYear={estimateListYear}
                availableYears={availableEstimateYears}
                search={estimateListSearchDraft}
                isSearchActive={Boolean(estimateListSearch.trim())}
                pagination={estimateListPagination}
                onYearChange={(year) => {
                  setEstimateListYear(year);
                  setEstimateListPage(1);
                }}
                onSearchChange={setEstimateListSearchDraft}
                onPageChange={(page) => {
                  if (page < 1 || page > Number(estimateListPagination.total_pages || 1)) return;
                  setEstimateListPage(page);
                }}
              />
            }
            onEdit={handleEditEstimate}
            onDuplicate={handleDuplicateEstimate}
            onDelete={handleDeleteEstimate}
            onViewPrint={handleViewPrint}
            onSetStatus={handleSetStatus}
            onConvertToInvoice={handleConvertToInvoice}
          />
        </>
      )}

      {view === 'entry' && (
        <>
          <div style={{ marginBottom: '12px' }}>
            <button
              style={hoverBlue(styles.backBtn, 'back')}
              onMouseEnter={() => setHoverKey('back')}
              onMouseLeave={() => setHoverKey('')}
              onClick={() => setView('list')}
            >
              ← BACK
            </button>
          </div>

          <EstimateEntry
            estimate={estimateDraft}
            setEstimate={setEstimateDraft}
            customers={customers}
            inventory={inventory}
            setInventory={setInventory}
            cannedJobs={cannedJobs}
            counties={counties}
            coupons={coupons}
            onSave={handleSaveEstimate}
          />
        </>
      )}

      {view === 'print' && printEstimate && (
        <>
          <div style={styles.viewToolbar}>
            <button
              style={hoverBlue(styles.backBtn, 'back2')}
              onMouseEnter={() => setHoverKey('back2')}
              onMouseLeave={() => setHoverKey('')}
              onClick={() => setView('list')}
            >
              ← BACK
            </button>

            <button
              style={hoverBlue(styles.printBtn, 'print')}
              onMouseEnter={() => setHoverKey('print')}
              onMouseLeave={() => setHoverKey('')}
              onClick={() => window.print()}
            >
              PRINT / SAVE PDF
            </button>

            <button
              style={hoverBlue(styles.editBtn, 'edit')}
              onMouseEnter={() => setHoverKey('edit')}
              onMouseLeave={() => setHoverKey('')}
              onClick={() => {
                setEstimateDraft(recalcTotals(printEstimate));
                setView('entry');
              }}
            >
              EDIT THIS ESTIMATE
            </button>
          </div>

          <div style={styles.printNote}>
            TIP: On the print screen, choose "Save as PDF" to download.
          </div>

          {printArea}
        </>
      )}
    </div>
  );
};

export default Estimates;
